// Central registry of bundled character portraits.
//
// All Jozori character art lives in this folder as optimized
// 512×512 WebP (primary) + JPG (fallback). Importing through this
// module guarantees:
//   1. One source of truth across web, mobile, emails, rewards, etc.
//   2. Vite hashes + long-term caches the files automatically.
//   3. Components can render <picture> with WebP and JPG fallback.
//
// To add a new character: drop `{code}.webp` and `{code}.jpg` in this
// folder, then add an entry below keyed by the DB `characters.code`.

import jadWebp from "./jad.webp";
import jadJpg from "./jad.jpg";
import juliaWebp from "./julia.webp";
import juliaJpg from "./julia.jpg";
import anasWebp from "./anas.webp";
import anasJpg from "./anas.jpg";
import taliaWebp from "./talia.webp";
import taliaJpg from "./talia.jpg";
import yousefWebp from "./yousef.webp";
import yousefJpg from "./yousef.jpg";
import lunaWebp from "./lina.webp";
import lunaJpg from "./lina.jpg";

export type CharacterAsset = { webp: string; jpg: string };

export const CHARACTER_ASSETS: Record<string, CharacterAsset> = {
  jad: { webp: jadWebp, jpg: jadJpg },
  julia: { webp: juliaWebp, jpg: juliaJpg },
  anas: { webp: anasWebp, jpg: anasJpg },
  talia: { webp: taliaWebp, jpg: taliaJpg },
  yousef: { webp: yousefWebp, jpg: yousefJpg },
  luna: { webp: lunaWebp, jpg: lunaJpg },
  lina: { webp: lunaWebp, jpg: lunaJpg },
};

/** Returns the bundled asset pair for a character code, or null. */
export function getCharacterAsset(code: string | null | undefined): CharacterAsset | null {
  if (!code) return null;
  return CHARACTER_ASSETS[code] ?? null;
}

/**
 * Resolve the best image src for a character. Prefers the bundled
 * WebP (fast, hashed, cached) and falls back to whatever
 * `avatar_url` the database has (useful while new characters are
 * being added before their bundled assets ship).
 */
export function characterImageSrc(
  code: string | null | undefined,
  avatarUrl: string | null | undefined,
): string | null {
  const a = getCharacterAsset(code);
  if (a) return a.webp;
  return avatarUrl ?? null;
}
